import { type Component, createSignal, createResource, createEffect, createMemo, For, Index, Show } from 'solid-js'
import { useNavigate, useParams } from '@solidjs/router'
import { Icon } from '@iconify-icon/solid'
import { usePolls } from '../../../shared/providers/coris/coris-polls.provider'
import { useUser } from '../../../shared/providers/user-provider'
import { AppRoutes } from '../../../shared/app-routes'
import { Skeleton, SkeletonLine } from '../../../components/shared/components/Skeleton'
import { SEGMENT_BG_CLASSES, SEGMENT_FILL_CLASSES } from './segment-colors'

// Mirrors the poll form card so loading the detail/results doesn't flash an
// empty editable form before the real data (and closed/read-only state) lands.
const PollFormSkeleton: Component = () => (
  <Skeleton class="relative flex flex-col gap-5 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
    <SkeletonLine class="absolute top-4 right-4 h-5 w-16 rounded-full" />
    <div class="flex flex-col gap-1.5">
      <SkeletonLine class="h-4 w-20" />
      <SkeletonLine class="h-10 w-full" />
    </div>
    <div class="flex flex-col gap-1.5">
      <SkeletonLine class="h-4 w-20" />
      <SkeletonLine class="h-10 w-full" />
      <SkeletonLine class="h-3 w-3/4" />
    </div>
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <SkeletonLine class="h-4 w-16" />
        <SkeletonLine class="h-3 w-20" />
      </div>
      <SkeletonLine class="h-2 w-full rounded-full" />
      <For each={[0, 1, 2]}>{() => <SkeletonLine class="h-10 w-full" />}</For>
    </div>
    <div class="flex items-center justify-between border-t border-gray-200 pt-4">
      <div class="flex gap-2">
        <SkeletonLine class="h-9 w-20" />
        <SkeletonLine class="h-9 w-28" />
      </div>
      <div class="flex gap-2">
        <SkeletonLine class="h-9 w-20" />
        <SkeletonLine class="h-9 w-20" />
      </div>
    </div>
  </Skeleton>
)

const toLocalInputValue = (d: Date): string => {
  // <input type="datetime-local"> wants 'YYYY-MM-DDTHH:mm' in local tz.
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const fromLocalInputValue = (s: string): Date => new Date(s)

const PollForm: Component = () => {
  const navigate = useNavigate()
  const params = useParams<{ postId?: string }>()
  const { getPoll, savePoll, closePoll, deletePoll, getLinkablePosts } = usePolls()
  const { organizationSlug } = useUser()

  const isEdit = (): boolean => !!params.postId
  const editPostId = (): number | null => (params.postId ? Number(params.postId) : null)

  // Autocomplete state for picking the forum post to attach the poll to.
  const [postQuery, setPostQuery] = createSignal('')
  const [selectedPost, setSelectedPost] = createSignal<{ id: number; title: string } | null>(null)
  const [showSuggestions, setShowSuggestions] = createSignal(false)

  // Backend doesn't accept a search param, so load the post list once per
  // org and filter client-side as the user types.
  const [allPosts] = createResource(
    () => {
      if (isEdit()) return null
      const slug = organizationSlug()
      if (!slug) return null
      return slug
    },
    async () => await getLinkablePosts.execute('', 200)
  )

  const postSuggestions = createMemo(() => {
    const list = allPosts()
    if (!list) return []
    const term = postQuery().trim().toLowerCase()
    const filtered = term
      ? list.filter((p) => p.title.toLowerCase().includes(term) || String(p.id).includes(term))
      : list
    return filtered.slice(0, 20)
  })

  const [question, setQuestion] = createSignal('')
  const [closesAt, setClosesAt] = createSignal(toLocalInputValue(new Date(Date.now() + 24 * 60 * 60_000)))
  const [options, setOptions] = createSignal<string[]>(['', ''])
  const [error, setError] = createSignal<string | null>(null)
  const [isSaving, setIsSaving] = createSignal(false)
  const [highlightedIdx, setHighlightedIdx] = createSignal<number | null>(null)

  // Load existing poll in edit mode.
  const [existing] = createResource(
    () => {
      const slug = organizationSlug()
      const postId = editPostId()
      if (!slug || postId === null) return null
      return { slug, postId }
    },
    async (s) => await getPoll.execute(s.postId)
  )

  createEffect(() => {
    const e = existing()
    if (!e) return
    setQuestion(e.question)
    setClosesAt(toLocalInputValue(e.closesAt))
    setOptions(e.options.map((o) => o.text))
  })

  // Polls open on create; the only states are 'open' and 'closed'.
  // Editing a live poll is allowed but warns; closed polls are read-only.
  const state = () => existing()?.state ?? 'open'
  const readOnly = (): boolean => isEdit() && state() === 'closed'
  const showEditWarning = (): boolean => isEdit() && state() === 'open'

  const handleOptionChange = (idx: number, value: string): void => {
    const next = [...options()]
    next[idx] = value
    setOptions(next)
  }
  const addOption = (): void => {
    if (options().length < 10) setOptions([...options(), ''])
  }
  const removeOption = (idx: number): void => {
    if (options().length <= 2) return
    setOptions(options().filter((_, i) => i !== idx))
  }

  const friendlyError = (e: unknown): string => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const code = (e as any)?.response?.data?.error
    if (typeof code === 'string') {
      const map: Record<string, string> = {
        POLL_ALREADY_EXISTS: 'This post already has a poll.',
        POLL_NOT_FOUND: 'Poll not found.',
        POLL_NOT_DRAFT: 'The poll is open or closed — structural edits are blocked.',
        POLL_NOT_OPEN: 'The poll is not open.',
        POLL_MIN_OPTIONS: 'A poll needs at least 2 options.',
        POLL_MAX_OPTIONS: 'A poll can have at most 10 options.',
        POLL_OPTION_EMPTY: 'One of the options is empty.',
        POLL_OPTION_TOO_LONG: 'An option is too long (max 120 chars).',
        POLL_OPTION_DUPLICATE: 'Duplicate option text.',
        POLL_TIME_WINDOW_INVALID: 'Close time must be after start time.',
        POLL_TIME_WINDOW_TOO_SHORT: 'The poll window is too short.',
        POLL_QUESTION_REQUIRED: 'Question is required.',
        POLL_QUESTION_TOO_LONG: 'Question is too long (max 280 chars).',
        FORBIDDEN: 'You do not have permission for this action.'
      }
      return map[code] ?? code
    }
    return e instanceof Error ? e.message : 'Something went wrong.'
  }

  const handleSubmit = async (e: SubmitEvent): Promise<void> => {
    e.preventDefault()
    if (isSaving()) return
    setError(null)
    setIsSaving(true)
    try {
      const optionInputs = options()
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .map((text) => ({ text }))
      if (isEdit()) {
        if (showEditWarning()) {
          const ok = window.confirm(
            'You are editing a live poll. Existing voters may be confused by changes to the question or options. Are you sure?'
          )
          if (!ok) {
            setIsSaving(false)
            return
          }
        }
        await savePoll.update(editPostId()!, {
          question: question(),
          closesAt: fromLocalInputValue(closesAt()),
          options: optionInputs
        })
      } else {
        const picked = selectedPost()
        if (!picked) throw new Error('Pick a forum post to attach this poll to.')
        await savePoll.create({
          postId: picked.id,
          question: question(),
          closesAt: fromLocalInputValue(closesAt()),
          options: optionInputs
        })
        navigate(AppRoutes.PollsEditId(String(picked.id)))
        return
      }
      navigate(AppRoutes.Polls())
    } catch (err) {
      setError(friendlyError(err))
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = async (): Promise<void> => {
    const pid = editPostId()
    if (pid === null) return
    setError(null)
    try {
      await closePoll.execute(pid)
      navigate(AppRoutes.Polls())
    } catch (err) {
      setError(friendlyError(err))
    }
  }

  const handleDelete = async (): Promise<void> => {
    const pid = editPostId()
    if (pid === null) return
    setError(null)
    try {
      await deletePoll.execute(pid)
      navigate(AppRoutes.Polls())
    } catch (err) {
      setError(friendlyError(err))
    }
  }

  return (
    <div class="flex w-full flex-col p-6">
      <div class="mx-auto w-full max-w-[800px]">
        <Show when={isEdit() && existing.loading}>
          <PollFormSkeleton />
        </Show>
        <Show when={!isEdit() || !existing.loading}>
          <Show when={showEditWarning()}>
            <div class="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900">
              <strong class="font-medium">Heads up — this poll is live.</strong> Voters may have already cast their
              ballots. Changing the question or options can confuse them; existing votes stay attached to the options
              they originally targeted. You can still extend or shorten
              <code class="mx-1">closesAt</code> safely.
            </div>
          </Show>

          <Show when={error()}>
            <div class="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error()}</div>
          </Show>

          <form
            onSubmit={handleSubmit}
            class="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col gap-5"
          >
            <Show when={isEdit() && existing()}>
              <span
                class="absolute top-4 right-4 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                classList={{
                  'bg-green-100 text-green-800': state() === 'open',
                  'bg-gray-100 text-gray-700': state() === 'closed'
                }}
              >
                {state().toUpperCase()}
              </span>
            </Show>
            <Show when={!isEdit()}>
              <div class="flex flex-col gap-1.5 relative">
                <span class="text-sm font-medium text-gray-700">Forum post</span>
                <Show when={selectedPost()}>
                  <div class="flex items-center justify-between gap-2 border border-primary-300 bg-primary-50 rounded-md px-3 py-2">
                    <div class="min-w-0">
                      <span class="text-xs text-gray-500">#{selectedPost()!.id}</span>
                      <span class="text-sm text-gray-900 ml-2 truncate">{selectedPost()!.title}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPost(null)
                        setPostQuery('')
                      }}
                      class="w-7 h-7 rounded-md flex items-center justify-center text-gray-500 hover:bg-white"
                      title="Change post"
                    >
                      <Icon icon="heroicons:x-mark" class="text-[1rem]" />
                    </button>
                  </div>
                </Show>
                <Show when={!selectedPost()}>
                  <input
                    type="text"
                    required
                    value={postQuery()}
                    onInput={(e) => {
                      setPostQuery(e.currentTarget.value)
                      setShowSuggestions(true)
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => {
                      // Delay so a click on a suggestion registers before we hide.
                      setTimeout(() => setShowSuggestions(false), 120)
                    }}
                    placeholder="Search posts by title or id…"
                    class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <Show when={showSuggestions()}>
                    <ul
                      role="listbox"
                      class="absolute z-20 left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg"
                    >
                      <Show when={allPosts.loading}>
                        <li class="px-3 py-2 text-sm text-gray-500">Loading…</li>
                      </Show>
                      <Show when={!allPosts.loading && postSuggestions().length === 0}>
                        <li class="px-3 py-2 text-sm text-gray-500">No posts found.</li>
                      </Show>
                      <For each={postSuggestions()}>
                        {(p) => (
                          <li>
                            <button
                              type="button"
                              disabled={p.hasPoll}
                              onMouseDown={(e) => {
                                // mousedown so we beat the input blur.
                                e.preventDefault()
                                if (p.hasPoll) return
                                setSelectedPost({ id: p.id, title: p.title })
                                setShowSuggestions(false)
                                setPostQuery('')
                              }}
                              class="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span class="text-xs text-gray-500 font-mono shrink-0">#{p.id}</span>
                              <span class="truncate flex-1 text-gray-900">{p.title}</span>
                              <Show when={p.hasPoll}>
                                <span class="text-xs text-gray-500 shrink-0">has poll</span>
                              </Show>
                            </button>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </Show>
                <span class="text-xs text-gray-500">
                  Search by title or post id. Posts that already have a poll are disabled.
                </span>
              </div>
            </Show>

            <label class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-gray-700">Question</span>
              <input
                type="text"
                maxlength="280"
                required
                disabled={readOnly()}
                value={question()}
                onInput={(e) => setQuestion(e.currentTarget.value)}
                class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>

            <label class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-gray-700">Closes at</span>
              <input
                type="datetime-local"
                required
                disabled={readOnly()}
                value={closesAt()}
                onInput={(e) => setClosesAt(e.currentTarget.value)}
                class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
              <span class="text-xs text-gray-500">
                The poll opens immediately on create and stays open until this time, or until you close it manually.
              </span>
            </label>

            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Options</span>
                <Show when={isEdit() && existing()}>
                  <span class="text-xs text-gray-500">{existing()!.totalVotes} total votes</span>
                </Show>
              </div>
              <Show when={isEdit() && existing()}>
                <Show
                  when={existing()!.totalVotes > 0}
                  fallback={<span class="text-xs text-gray-400">No votes yet</span>}
                >
                  <div class="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <For each={existing()!.options}>
                      {(opt, i) => (
                        <Show when={opt.voteCount > 0}>
                          <div
                            class={`${
                              SEGMENT_BG_CLASSES[i() % SEGMENT_BG_CLASSES.length]
                            } cursor-pointer transition-[width,opacity,transform] duration-200 ease-out origin-center`}
                            classList={{
                              'opacity-100 scale-y-150 z-10 shadow-sm': highlightedIdx() === i(),
                              'opacity-30': highlightedIdx() !== null && highlightedIdx() !== i(),
                              'opacity-90': highlightedIdx() === null
                            }}
                            style={{ width: `${(opt.voteCount / existing()!.totalVotes) * 100}%` }}
                            onMouseEnter={() => setHighlightedIdx(i())}
                            onMouseLeave={() => setHighlightedIdx(null)}
                            title={`${opt.text}: ${opt.voteCount}`}
                          />
                        </Show>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
              <Index each={options()}>
                {(opt, i) => {
                  const existingOption = (): { voteCount: number } | undefined => existing()?.options[i]
                  const pct = (): number => {
                    const total = existing()?.totalVotes ?? 0
                    const v = existingOption()?.voteCount ?? 0
                    if (total === 0) return 0
                    return Math.round((v / total) * 100)
                  }
                  const fillClass = (): string => SEGMENT_FILL_CLASSES[i % SEGMENT_FILL_CLASSES.length]
                  return (
                    <div
                      class="relative flex items-center rounded-md bg-gray-50 overflow-hidden focus-within:ring-1 focus-within:ring-primary-500 transition-shadow duration-150"
                      classList={{
                        'bg-gray-100': readOnly(),
                        'ring-1 ring-gray-300': highlightedIdx() === i && !readOnly()
                      }}
                      onMouseEnter={() => setHighlightedIdx(i)}
                      onMouseLeave={() => setHighlightedIdx(null)}
                    >
                      <Show when={isEdit() && existingOption() && existing()!.totalVotes > 0}>
                        <div
                          class={`pointer-events-none absolute inset-y-0 left-0 ${fillClass()} transition-[width,opacity] duration-200 ease-out`}
                          classList={{
                            'opacity-100': highlightedIdx() === i || highlightedIdx() === null,
                            'opacity-40': highlightedIdx() !== null && highlightedIdx() !== i
                          }}
                          style={{ width: `${pct()}%` }}
                        />
                      </Show>
                      <input
                        type="text"
                        maxlength="120"
                        required
                        disabled={readOnly()}
                        value={opt()}
                        onInput={(e) => handleOptionChange(i, e.currentTarget.value)}
                        onFocus={() => setHighlightedIdx(i)}
                        onBlur={() => setHighlightedIdx(null)}
                        placeholder={`Option ${i + 1}`}
                        class="relative flex-1 border-0 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-0 disabled:text-gray-500"
                      />
                      <Show when={isEdit() && existingOption()}>
                        <span class="relative shrink-0 pr-3 text-xs tabular-nums text-gray-600">
                          {existingOption()!.voteCount} · {pct()}%
                        </span>
                      </Show>
                      <Show when={!readOnly() && options().length > 2}>
                        <button
                          type="button"
                          onClick={() => removeOption(i)}
                          class="relative shrink-0 w-9 h-9 mr-1 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100"
                          title="Remove option"
                        >
                          <Icon icon="heroicons:x-mark" class="text-[1rem]" />
                        </button>
                      </Show>
                    </div>
                  )
                }}
              </Index>
              <Show when={!readOnly() && options().length < 10}>
                <button
                  type="button"
                  onClick={addOption}
                  class="self-start flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
                >
                  <Icon icon="heroicons:plus" class="text-[1rem]" />
                  Add option
                </button>
              </Show>
            </div>

            <div class="flex items-center justify-between pt-4 border-t border-gray-200">
              <div class="flex items-center gap-2">
                <Show when={isEdit() && state() === 'open'}>
                  <button
                    type="button"
                    onClick={handleDelete}
                    class="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700"
                  >
                    Close poll now
                  </button>
                </Show>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(AppRoutes.Polls())}
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <Show when={!readOnly()}>
                  <button
                    type="submit"
                    disabled={isSaving()}
                    class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSaving() ? 'Saving…' : isEdit() ? 'Save' : 'Create'}
                  </button>
                </Show>
              </div>
            </div>
          </form>
        </Show>
      </div>
    </div>
  )
}

export default PollForm
